import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Collapse, Input, Modal, Spin, Tag, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../../../utils/constants";
import { Action } from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";

const { Text } = Typography;

interface Props {
  showModal: boolean;
  onClose: () => void;
  userId: number | null;
  userEmail: string;
  api: NotificationInstance;
}

const ManageActionsModal: React.FC<Props> = ({ showModal, onClose, userId, userEmail, api }) => {
  const [allActions, setAllActions] = useState<Action[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [initialSelectedKeys, setInitialSelectedKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [activeCategories, setActiveCategories] = useState<string[]>([]);

  useEffect(() => {
    if (!showModal || userId === null) return;
    setLoading(true);
    setSearchText("");
    Promise.all([
      axios.get(`${BASE_URL}actions`),
      axios.get(`${BASE_URL}get_user_actions/${userId}`),
    ])
      .then(([actionsResponse, userActionsResponse]) => {
        setAllActions(actionsResponse.data);
        setSelectedKeys(userActionsResponse.data.content);
        setInitialSelectedKeys(userActionsResponse.data.content);
      })
      .catch((error) => {
        api.error({
          title: "Error Fetching Actions",
          description: `Failed to fetch actions for this user: ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .finally(() => setLoading(false));
  }, [showModal, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const actionsByCategory = useMemo(() => {
    return allActions.reduce<Record<string, Action[]>>((groups, action) => {
      const categoryName = action.action_category?.name ?? "Uncategorized";
      (groups[categoryName] ||= []).push(action);
      return groups;
    }, {});
  }, [allActions]);

  // Once actions load, start with only the categories that already have a
  // grant expanded - keeps a 7+ category list from opening as one long wall
  // of checkboxes. Deliberately not reactive to selectedKeys after that, so
  // toggling a checkbox doesn't fight the admin's own expand/collapse.
  useEffect(() => {
    if (allActions.length === 0) return;
    const withSelection = Object.entries(actionsByCategory)
      .filter(([, actions]) => actions.some((a) => selectedKeys.includes(a.key)))
      .map(([name]) => name);
    setActiveCategories(withSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allActions]);

  const filteredActionsByCategory = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) return actionsByCategory;
    const filtered: Record<string, Action[]> = {};
    Object.entries(actionsByCategory).forEach(([categoryName, actions]) => {
      const matches = actions.filter(
        (a) => a.name.toLowerCase().includes(search) || a.key.toLowerCase().includes(search)
      );
      if (matches.length > 0) filtered[categoryName] = matches;
    });
    return filtered;
  }, [actionsByCategory, searchText]);

  // Auto-expand whatever matches while the admin is actively searching.
  useEffect(() => {
    if (searchText.trim()) {
      setActiveCategories(Object.keys(filteredActionsByCategory));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const isDirty = useMemo(() => {
    if (selectedKeys.length !== initialSelectedKeys.length) return true;
    const initialSet = new Set(initialSelectedKeys);
    return selectedKeys.some((k) => !initialSet.has(k));
  }, [selectedKeys, initialSelectedKeys]);

  const toggleAction = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  };

  const toggleCategory = (categoryActions: Action[], checked: boolean) => {
    const categoryKeys = categoryActions.map((a) => a.key);
    setSelectedKeys((prev) =>
      checked
        ? Array.from(new Set([...prev, ...categoryKeys]))
        : prev.filter((k) => !categoryKeys.includes(k))
    );
  };

  const handleSave = () => {
    if (userId === null) return;
    setSaving(true);
    axios
      .post(`${BASE_URL}update_user/${userId}`, { action_keys: selectedKeys })
      .then(() => {
        onClose();
        api.success({
          title: "Actions Updated",
          description: `Actions for ${userEmail} were updated successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        api.error({
          title: "Error Updating Actions",
          description: `Failed to update actions for ${userEmail}: ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .finally(() => setSaving(false));
  };

  const collapseItems = Object.entries(filteredActionsByCategory).map(([categoryName, categoryActions]) => {
    const categoryKeys = categoryActions.map((a) => a.key);
    const selectedInCategory = categoryKeys.filter((k) => selectedKeys.includes(k));
    const allSelected = selectedInCategory.length === categoryKeys.length;
    const someSelected = selectedInCategory.length > 0 && !allSelected;

    return {
      key: categoryName,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => toggleCategory(categoryActions, e.target.checked)}
            />
          </span>
          <Text strong>{categoryName}</Text>
          <Tag color={selectedInCategory.length > 0 ? "blue" : "default"}>
            {selectedInCategory.length}/{categoryKeys.length}
          </Tag>
        </div>
      ),
      children: (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "8px 16px",
          }}
        >
          {categoryActions.map((action) => (
            <Checkbox
              key={action.key}
              checked={selectedKeys.includes(action.key)}
              onChange={(e) => toggleAction(action.key, e.target.checked)}
            >
              {action.name}
            </Checkbox>
          ))}
        </div>
      ),
    };
  });

  return (
    <Modal
      title={`Manage Actions for ${userEmail}`}
      open={showModal}
      onCancel={onClose}
      width={720}
      closable={!saving}
      mask={{ closable: !saving }}
      footer={[
        <Button key="close" onClick={onClose} disabled={saving}>Close</Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!isDirty || saving}
          onClick={handleSave}
        >Save Changes</Button>,
      ]}
    >
      <Input
        placeholder="Search actions..."
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />
      <Spin size="large" spinning={loading}>
        <div style={{ maxHeight: "55vh", overflowY: "auto", paddingRight: 4 }}>
          {collapseItems.length > 0 ? (
            <Collapse
              items={collapseItems}
              activeKey={activeCategories}
              onChange={(keys) => setActiveCategories(keys as string[])}
            />
          ) : (
            !loading && (
              <p>{allActions.length === 0 ? "No actions defined yet." : "No actions match your search."}</p>
            )
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ManageActionsModal;
